import {
  MessageSquareText,
  ListChecks,
  Layers,
  NotebookPen,
  BookOpenCheck,
  Repeat2,
  Lightbulb,
  ScrollText,
  Zap,
  Image as ImageIcon,
  ScanSearch,
  Video,
  FileText,
  TrendingUp,
  BarChart3,
  BriefcaseBusiness,
  SearchCheck,
  Workflow,
} from "lucide-react";
import { Reveal } from "./Reveal";
import { PhoneMockup } from "./PhoneMockup";

const worlds = [
  {
    id: "study",
    index: "01",
    name: "STUDY",
    chip: "bg-oriva-cyan",
    text: "text-oriva-cyan",
    title: "Learn faster, remember longer.",
    desc: "Your personalized study workspace — generate quizzes, rehearse with a mock viva, drill flashcards and turn rough notes into structured knowledge.",
    phone: "/assets/app-study-hub.webp",
    phoneAlt: "ORIVA app — Study Hub",
    tools: [
      { icon: ListChecks, name: "Generate Quiz" },
      { icon: MessageSquareText, name: "Mock Viva" },
      { icon: Layers, name: "Flashcards" },
      { icon: NotebookPen, name: "Notes" },
      { icon: BookOpenCheck, name: "Homework" },
      { icon: Repeat2, name: "Revision" },
    ],
  },
  {
    id: "create",
    index: "02",
    name: "CREATE",
    chip: "bg-oriva-gold",
    text: "text-oriva-gold",
    title: "From blank page to published.",
    desc: "Ideation, scripting and packaging tools for creators — go from a rough idea to a hook, a script, a thumbnail and a finished piece of content.",
    image:
      "https://images.pexels.com/photos/12433032/pexels-photo-12433032.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    imageAlt: "Content creator working in a studio",
    tools: [
      { icon: Lightbulb, name: "Content Ideas" },
      { icon: ScrollText, name: "Script Generation" },
      { icon: Zap, name: "Hook Generation" },
      { icon: ImageIcon, name: "Image Generation" },
      { icon: ScanSearch, name: "Thumbnail Analyzer" },
      { icon: Video, name: "Video Analysis" },
    ],
  },
  {
    id: "work",
    index: "03",
    name: "WORK",
    chip: "bg-oriva-coral",
    text: "text-oriva-coral",
    title: "Professional output, on demand.",
    desc: "Productivity workflows for professionals and founders — documents, research, data and strategy handled by one capable assistant.",
    image:
      "https://images.unsplash.com/photo-1706689656095-168768dc20a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjB3b3JraW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc4NzM5NjgyMnww&ixlib=rb-4.1.0&q=85",
    imageAlt: "Professional working in a modern office",
    tools: [
      { icon: FileText, name: "Document Tools" },
      { icon: TrendingUp, name: "SEO Optimizer" },
      { icon: BarChart3, name: "Data Analysis" },
      { icon: BriefcaseBusiness, name: "Business Planning" },
      { icon: SearchCheck, name: "Research Assistant" },
      { icon: Workflow, name: "Pro Workflows" },
    ],
  },
];

export const Worlds = () => (
  <section id="worlds" className="bg-white py-24 sm:py-32" data-testid="worlds-section">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">The three worlds</p>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
          One assistant. Three specialised minds.
        </h2>
        <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">
          ORIVA is a universal AI assistant at its core — with deep, purpose-built
          toolkits for the three places you spend your life.
        </p>
      </Reveal>

      <div className="mt-20 flex flex-col gap-24">
        {worlds.map((world, w) => (
          <div
            key={world.id}
            className="grid items-start gap-10 lg:grid-cols-12"
            data-testid={`world-${world.id}`}
          >
            <Reveal className={`lg:col-span-4 ${w % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="flex items-center gap-3">
                <span className={`h-2 w-2 rounded-full ${world.chip}`} />
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
                  {world.index} / {world.name}
                </span>
              </div>
              <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                {world.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">{world.desc}</p>
            </Reveal>

            <div className={`lg:col-span-8 ${w % 2 === 1 ? "lg:order-1" : ""}`}>
              <Reveal delay={0.1}>
                {world.phone ? (
                  <div className="mx-auto max-w-[280px]" style={{ perspective: "1000px" }}>
                    <PhoneMockup src={world.phone} alt={world.phoneAlt} tilt={-4} />
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-2xl border border-slate-200">
                    <img
                      src={world.image}
                      alt={world.imageAlt}
                      loading="lazy"
                      className="aspect-[16/7] w-full object-cover"
                    />
                  </div>
                )}
              </Reveal>
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {world.tools.map((tool, t) => (
                  <Reveal key={tool.name} delay={0.12 + t * 0.05} y={16}>
                    <div
                      className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3.5 transition-colors duration-200 hover:border-slate-300 hover:bg-slate-50"
                      data-testid={`world-${world.id}-tool-${t}`}
                    >
                      <tool.icon className={`h-4.5 w-4.5 shrink-0 ${world.text}`} size={18} />
                      <span className="text-sm font-medium text-slate-800">{tool.name}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
